import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, CloudSun, Cloud, CloudFog, CloudRain, Snowflake, CloudLightning } from 'lucide-react';
import './WeatherWidget.css';

const weatherTranslations = {
  it: {
    clear: 'Sereno', partlyCloudy: 'Poco nuvoloso', cloudy: 'Nuvoloso',
    fog: 'Nebbia', rain: 'Piovoso', snow: 'Neve', thunderstorm: 'Temporale', tomorrow: 'domani'
  },
  en: {
    clear: 'Clear', partlyCloudy: 'Partly cloudy', cloudy: 'Cloudy',
    fog: 'Fog', rain: 'Rainy', snow: 'Snow', thunderstorm: 'Thunderstorm', tomorrow: 'tomorrow'
  },
  de: {
    clear: 'Klar', partlyCloudy: 'Leicht bewölkt', cloudy: 'Bewölkt',
    fog: 'Nebel', rain: 'Regnerisch', snow: 'Schnee', thunderstorm: 'Gewitter', tomorrow: 'morgen'
  },
  nl: {
    clear: 'Helder', partlyCloudy: 'Licht bewolkt', cloudy: 'Bewolkt',
    fog: 'Mist', rain: 'Regenachtig', snow: 'Sneeuw', thunderstorm: 'Onweer', tomorrow: 'morgen'
  },
  fr: {
    clear: 'Clair', partlyCloudy: 'Peu nuageux', cloudy: 'Nuageux',
    fog: 'Brouillard', rain: 'Pluvieux', snow: 'Neige', thunderstorm: 'Orage', tomorrow: 'demain'
  }
};

const getWeatherType = (code) => {
  if (code === 0 || code === 1) return 'clear';
  if (code === 2) return 'partlyCloudy';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 67) return 'rain';
  if (code >= 71 && code <= 77) return 'snow';
  if (code >= 80 && code <= 82) return 'rain';
  if (code >= 85 && code <= 86) return 'snow';
  if (code >= 95 && code <= 99) return 'thunderstorm';
  return 'clear';
};

const WeatherIcon = ({ type }) => {
  const props = { strokeWidth: 1.5, size: "100%" };
  switch (type) {
    case 'clear': return <Sun {...props} />;
    case 'partlyCloudy': return <CloudSun {...props} />;
    case 'cloudy': return <Cloud {...props} />;
    case 'fog': return <CloudFog {...props} />;
    case 'rain': return <CloudRain {...props} />;
    case 'snow': return <Snowflake {...props} />;
    case 'thunderstorm': return <CloudLightning {...props} />;
    default: return <Sun {...props} />;
  }
};

const WeatherWidget = () => {
  const { i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(() => {
    return localStorage.getItem('weatherWidgetClosed') !== new Date().toDateString();
  });
  const [weather, setWeather] = useState(null);
  
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch('https://api.open-meteo.com/v1/forecast?latitude=46.0153&longitude=11.9064&current_weather=true&daily=weathercode,temperature_2m_max&timezone=Europe%2FRome');
        const data = await res.json();
        
        setWeather({
          currentTemp: Math.round(data.current_weather.temperature),
          currentCode: data.current_weather.weathercode,
          tomorrowTemp: Math.round(data.daily.temperature_2m_max[1]),
          tomorrowCode: data.daily.weathercode[1]
        });
      } catch (e) {
        console.error("Failed to fetch weather", e);
      }
    };
    fetchWeather();
  }, []);

  if (!weather) return null;

  const currentLang = i18n.language?.split('-')[0] || 'it';
  const t = weatherTranslations[currentLang] || weatherTranslations['it'];
  
  const currentType = getWeatherType(weather.currentCode);
  const tomorrowType = getWeatherType(weather.tomorrowCode);
  
  let dateStr = new Intl.DateTimeFormat(currentLang, { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  }).format(new Date());
  
  dateStr = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('weatherWidgetClosed', new Date().toDateString());
  };

  const handleOpen = () => {
    setIsVisible(true);
    localStorage.removeItem('weatherWidgetClosed');
  };

  if (!isVisible) {
    return (
      <button 
        className="weather-reopen-btn" 
        onClick={handleOpen} 
        aria-label="Apri Meteo"
        title="Apri Meteo"
      >
        <WeatherIcon type={currentType} />
      </button>
    );
  }

  return (
    <div className="weather-widget">
      <div className="weather-widget-inner">
        <div className="weather-widget-bg" style={{ backgroundImage: "url('/images/piazza-maggiore.jpg')" }}></div>
        <div className="weather-widget-content">
          <div className="weather-left">
            <h2 className="weather-city">Feltre</h2>
            <div className="weather-details-bottom">
              <div className="weather-date">{dateStr}</div>
              <div className="weather-condition">
                {t[currentType]} 
                <div className="weather-icon"><WeatherIcon type={currentType} /></div>
              </div>
            </div>
          </div>
          <div className="weather-right">
            <div className="weather-temp">{weather.currentTemp}°C</div>
            <div className="weather-tomorrow">
              <span>{t.tomorrow}</span>
              <div className="weather-tomorrow-temp">
                {weather.tomorrowTemp}°C
                <div className="weather-icon"><WeatherIcon type={tomorrowType} /></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button className="weather-close" onClick={handleClose} aria-label="Chiudi">×</button>
    </div>
  );
};

export default WeatherWidget;
